import { createFileRoute } from '@tanstack/react-router';
import { Alert, Button, Group, Loader, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { ApiRequestError } from '../../../../api/client';
import { useCanOperate } from '../../../../auth/permissions';
import { ConfirmButton } from '../../../../components/ConfirmButton';
import { DocStatusBadge } from '../../../../components/DocStatusBadge';
import { CompleteCountButton } from '../../../../features/silo-counts/components/CompleteCountButton';
import { CountLinesEditor } from '../../../../features/silo-counts/components/CountLinesEditor';
import { PopulateFromStockButton } from '../../../../features/silo-counts/components/PopulateFromStockButton';
import { useCancelStockCount, useSites, useStartStockCount, useStockCount } from '../../../../features/silo-counts/api';

/** Stock count detail (BUILD_SPEC.md §8.6): start → enter counted quantities
 * per line → complete (posts variance adjustments). */
export const Route = createFileRoute('/_authenticated/silo-counts/counts/$countId')({
  component: CountDetailPage,
});

function CountDetailPage() {
  const { countId } = Route.useParams();
  const canOperate = useCanOperate();
  const countQuery = useStockCount(countId);
  const sitesQuery = useSites();
  const startCount = useStartStockCount(countId);
  const cancelCount = useCancelStockCount(countId);

  if (countQuery.isLoading) {
    return (
      <Group>
        <Loader size="sm" />
        <Text c="dimmed">Loading count…</Text>
      </Group>
    );
  }

  if (countQuery.isError || !countQuery.data) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Could not load count">
        {countQuery.error instanceof ApiRequestError ? countQuery.error.message : countQuery.error?.message}
      </Alert>
    );
  }

  const count = countQuery.data;
  const site = sitesQuery.data?.items.find((s) => s.id === count.siteId);
  const editable = count.status === 'IN_PROGRESS';
  const canCancel = count.status === 'DRAFT' || count.status === 'IN_PROGRESS';
  // "countedQty non-null = counted" (BUILD_SPEC.md §8.6, G-B) — a line populated
  // from stock on hand but never keyed in still shows as outstanding here.
  const countedLines = count.lines.filter((l) => l.countedQty !== null).length;
  const totalLines = count.lines.length;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>{count.number}</Title>
          <Text c="dimmed">{site?.name ?? count.siteId}</Text>
        </div>
        <div>
          <DocStatusBadge status={count.status} domain="stockCount" size="lg" />
          {totalLines > 0 && (
            <Text size="sm" c="dimmed" ta="right" mt={4}>
              {countedLines} of {totalLines} lines counted
            </Text>
          )}
        </div>
      </Group>

      {startCount.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {startCount.error instanceof ApiRequestError ? startCount.error.message : startCount.error?.message}
        </Alert>
      )}
      {cancelCount.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {cancelCount.error instanceof ApiRequestError ? cancelCount.error.message : cancelCount.error?.message}
        </Alert>
      )}

      <Group>
        {count.status === 'DRAFT' && (
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <Button disabled={!canOperate} loading={startCount.isPending} onClick={() => startCount.mutate()}>
                Start count
              </Button>
            </span>
          </Tooltip>
        )}
        {editable && <PopulateFromStockButton countId={count.id} />}
        {editable && count.lines.length > 0 && (
          <CompleteCountButton countId={count.id} siteId={count.siteId} lines={count.lines} />
        )}
        {canCancel && (
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <ConfirmButton
                variant="default"
                color="red"
                disabled={!canOperate}
                loading={cancelCount.isPending}
                label="Cancel count"
                message={`Cancel count ${count.number}? It will be marked CANCELLED with no ledger effect — any counted quantities entered so far are discarded.`}
                onConfirm={() => cancelCount.mutate()}
              />
            </span>
          </Tooltip>
        )}
      </Group>

      {count.status === 'DRAFT' ? (
        <Text c="dimmed" size="sm">
          Start the count to begin entering counted quantities.
        </Text>
      ) : (
        <CountLinesEditor countId={count.id} siteId={count.siteId} lines={count.lines} editable={editable} />
      )}
    </Stack>
  );
}
